import { useState } from "react";
import { getActiveDomain, listAccountConfigs } from "../api/config";
import { batchCreateUsers, createUser, listUsers } from "../api/users";
import type { BatchCreateSummary, CreateUserRequest } from "../api/types";
import { useAsync } from "../hooks/useAsync";
import { parseCsvUsers } from "./csvParser";
import { generateUsers } from "./nameGenerator";

const TIERS = ["pro", "proplus", "promax", "power"];

/**
 * 建号页,对应产品文档 §3.2。
 * 支持单条表单建号,以及粘贴 CSV 文本批量建号(不引入文件上传组件依赖,
 * 直接用 textarea 粘贴 CSV 内容更轻量)。
 * CSV 列顺序: account_id,username,given_name,family_name,email,tier
 */
export function CreateUserPage() {
  const accounts = useAsync(() => listAccountConfigs(), []);

  const [form, setForm] = useState<CreateUserRequest>({
    account_id: "",
    username: "",
    given_name: "",
    family_name: "",
    email: "",
    tier: "pro",
    auto_activate: true,
  });
  const [singleResult, setSingleResult] = useState<string | null>(null);
  const [singleError, setSingleError] = useState<string | null>(null);
  const [singleLoading, setSingleLoading] = useState(false);

  const [csvText, setCsvText] = useState("");
  const [batchResult, setBatchResult] = useState<BatchCreateSummary | null>(null);
  const [batchError, setBatchError] = useState<string | null>(null);
  const [batchLoading, setBatchLoading] = useState(false);
  // 批量建号"是否自动激活"默认值:应用于 CSV 里没有显式写第 7 列的行
  // (以及"自动生成用户信息"追加的新行),不强制覆盖用户在 CSV 里手动指定的值。
  const [batchAutoActivate, setBatchAutoActivate] = useState(true);

  // ---- 自动生成用户信息 ----
  const [genAccountId, setGenAccountId] = useState("");
  const [genTier, setGenTier] = useState("pro");
  const [genCount, setGenCount] = useState(5);
  const [genLoading, setGenLoading] = useState(false);
  const [genError, setGenError] = useState<string | null>(null);

  const handleSingleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSingleError(null);
    setSingleResult(null);
    setSingleLoading(true);
    try {
      const result = await createUser(form);
      setSingleResult(
        `建号成功: ${result.email} (user_id=${result.user_id}${result.already_existed ? ", 该用户已存在,幂等跳过" : ""})`
      );
    } catch (err) {
      setSingleError(err instanceof Error ? err.message : "建号失败");
    } finally {
      setSingleLoading(false);
    }
  };

  const handleBatchSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBatchError(null);
    setBatchResult(null);
    setBatchLoading(true);
    try {
      const users = parseCsvUsers(csvText, batchAutoActivate);
      if (users.length === 0) {
        setBatchError("CSV 内容为空");
        return;
      }
      const result = await batchCreateUsers(users);
      setBatchResult(result);
    } catch (err) {
      setBatchError(err instanceof Error ? err.message : "批量建号失败");
    } finally {
      setBatchLoading(false);
    }
  };

  /**
   * 自动生成用户信息:
   *   1. 读取当前生效收信域名(拼邮箱用)
   *   2. 读取该目标账号下已有用户的 username/email,连同当前输入框里
   *      已经存在的行一起作为去重集合,避免生成的用户名和数据库里已有的
   *      或本次还没提交的行重复
   *   3. 生成结果以 CSV 行的形式追加到输入框(不覆盖用户已经手动输入的内容)
   */
  const handleAutoGenerate = async () => {
    const accountId = genAccountId || accounts.data?.accounts[0]?.account_id || "";
    if (!accountId) {
      setGenError("请先选择目标账号");
      return;
    }
    if (genCount < 1 || genCount > 500) {
      setGenError("生成数量需在 1~500 之间");
      return;
    }
    setGenLoading(true);
    setGenError(null);
    try {
      const [domainResp, existingUsersResp] = await Promise.all([
        getActiveDomain(),
        // pageSize 拉大页:这里需要拿到该账号下的全部用户名做去重(§3.2),
        // 不能用列表页默认的每页 20 条(否则只对第一页去重,漏判)。
        listUsers(accountId, { pageSize: 1000 }),
      ]);
      const domain = domainResp.active_email_domain;
      if (!domain) {
        setGenError("尚未配置当前生效收信域名,请先在配置页设置(§4.1)");
        return;
      }

      const existingUsernames = new Set(existingUsersResp.users.map((u) => u.username.toLowerCase()));
      // 输入框里已有的行(可能是用户手动粘贴的、也可能是上一次生成还没提交的)
      // 也要一起去重,否则连续点两次"自动生成"会产出重复用户名。
      for (const row of parseCsvUsers(csvText, batchAutoActivate)) {
        if (row.username) existingUsernames.add(row.username.toLowerCase());
      }

      const generated = generateUsers(genCount, domain, existingUsernames);
      const autoActivateCol = batchAutoActivate ? "是" : "否";
      const newLines = generated
        .map(
          (u) =>
            `${accountId},${u.username},${u.given_name},${u.family_name},${u.email},${genTier},${autoActivateCol}`
        )
        .join("\n");

      setCsvText((prev) => (prev.trim() ? `${prev.trim()}\n${newLines}` : newLines));
    } catch (err) {
      setGenError(err instanceof Error ? err.message : "自动生成失败");
    } finally {
      setGenLoading(false);
    }
  };

  return (
    <div>
      <h2 className="section-title">批量建号</h2>

      <div className="card">
        <div className="section-title">单条建号</div>
        {singleError && <div className="error-banner">{singleError}</div>}
        {singleResult && (
          <div className="card" style={{ background: "#f0fdf4", color: "#166534" }}>
            {singleResult}
          </div>
        )}
        <form onSubmit={handleSingleSubmit}>
          <div className="form-row">
            <label>目标账号</label>
            <select
              value={form.account_id}
              onChange={(e) => setForm({ ...form, account_id: e.target.value })}
              required
            >
              <option value="">请选择</option>
              {accounts.data?.accounts.map((a) => (
                <option key={a.account_id} value={a.account_id}>
                  {a.account_id}
                </option>
              ))}
            </select>
          </div>
          <div className="form-row">
            <label>用户名</label>
            <input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} required />
          </div>
          <div className="form-row">
            <label>名</label>
            <input value={form.given_name} onChange={(e) => setForm({ ...form, given_name: e.target.value })} required />
          </div>
          <div className="form-row">
            <label>姓</label>
            <input value={form.family_name} onChange={(e) => setForm({ ...form, family_name: e.target.value })} required />
          </div>
          <div className="form-row">
            <label>邮箱</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
              placeholder="username@kiro-users.example.com"
            />
          </div>
          <div className="form-row">
            <label>层级</label>
            <select value={form.tier} onChange={(e) => setForm({ ...form, tier: e.target.value })}>
              {TIERS.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>
          <div className="form-row">
            <label>是否自动激活</label>
            <select
              value={form.auto_activate === false ? "no" : "yes"}
              onChange={(e) => setForm({ ...form, auto_activate: e.target.value !== "no" })}
            >
              <option value="yes">是(默认)</option>
              <option value="no">否</option>
            </select>
          </div>
          <p style={{ fontSize: 12, color: "#64748b", marginTop: -8 }}>
            选择"否"时不会执行"登录→填用户名→接收验证码→填入新密码"这一套自动化动作,
            该用户直接标记为人工处理,由客户自行完成登录和改密。主要用于邮箱不是平台
            控制的接码邮箱(平台收不到验证码邮件)的场景。
          </p>
          <button className="primary" type="submit" disabled={singleLoading}>
            {singleLoading ? "提交中..." : "建号"}
          </button>
        </form>
      </div>

      <div className="card">
        <div className="section-title">批量建号(粘贴 CSV)</div>
        <p style={{ fontSize: 12, color: "#64748b" }}>
          每行格式: account_id,username,given_name,family_name,email,tier,auto_activate(§3.2)。
          最后一列(auto_activate)可省略,省略时使用下方"是否自动激活"下拉框的值。
        </p>

        <div className="form-row">
          <label>是否自动激活(适用于未在 CSV 行内单独指定的记录)</label>
          <select
            value={batchAutoActivate ? "yes" : "no"}
            onChange={(e) => setBatchAutoActivate(e.target.value !== "no")}
          >
            <option value="yes">是(默认)</option>
            <option value="no">否</option>
          </select>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "flex-end",
            gap: 12,
            flexWrap: "wrap",
            padding: 12,
            marginBottom: 12,
            background: "#f8fafc",
            borderRadius: 6,
          }}
        >
          <div>
            <label style={{ display: "block", fontSize: 12, marginBottom: 4 }}>目标账号</label>
            <select value={genAccountId} onChange={(e) => setGenAccountId(e.target.value)}>
              <option value="">(默认第一个)</option>
              {accounts.data?.accounts.map((a) => (
                <option key={a.account_id} value={a.account_id}>
                  {a.account_id}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ display: "block", fontSize: 12, marginBottom: 4 }}>层级</label>
            <select value={genTier} onChange={(e) => setGenTier(e.target.value)}>
              {TIERS.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ display: "block", fontSize: 12, marginBottom: 4 }}>生成数量</label>
            <input
              type="number"
              min={1}
              max={500}
              value={genCount}
              onChange={(e) => setGenCount(Number(e.target.value))}
              style={{ width: 80 }}
            />
          </div>
          <button type="button" onClick={handleAutoGenerate} disabled={genLoading}>
            {genLoading ? "生成中..." : "自动生成用户信息"}
          </button>
          {genError && <span style={{ color: "#dc2626", fontSize: 12 }}>{genError}</span>}
        </div>

        {batchError && <div className="error-banner">{batchError}</div>}
        <form onSubmit={handleBatchSubmit}>
          <textarea
            style={{ width: "100%", height: 140, fontFamily: "monospace" }}
            value={csvText}
            onChange={(e) => setCsvText(e.target.value)}
            placeholder="222222222222,alice,Alice,Wang,alice@kiro-users.example.com,pro,是"
          />
          <div style={{ marginTop: 8 }}>
            <button className="primary" type="submit" disabled={batchLoading}>
              {batchLoading ? "提交中..." : "批量建号"}
            </button>
          </div>
        </form>

        {batchResult && (
          <div style={{ marginTop: 12 }}>
            <p>
              共 {batchResult.total} 条,成功 {batchResult.succeeded} 条,失败 {batchResult.failed} 条
            </p>
            <table>
              <thead>
                <tr>
                  <th>邮箱</th>
                  <th>结果</th>
                  <th>说明</th>
                </tr>
              </thead>
              <tbody>
                {batchResult.results.map((r) => (
                  <tr key={r.email}>
                    <td>{r.email}</td>
                    <td>{r.success ? "成功" : "失败"}</td>
                    <td>{r.error ?? (r.already_existed ? "已存在,幂等跳过" : "-")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
